/**
 * Pure functions for normalizing, filtering, and sorting the showcase
 * game portfolio. Kept React-free and side-effect-free so they can be
 * exercised in unit tests without rendering.
 */

import MAX_WIN_OVERRIDES from 'src/data/max-win-overrides';

export const FAMILY_KEYWORDS = [
  'caramelo', 'prosperity', 'firecrackers fortune',
  'fiesta', 'hades', 'thunder', 'pirates treasure',
  'shapes of fortune', 'big escape',
];

const ZODIAC_SIGNS = [
  'aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo',
  'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces',
];

export const detectFamily = (name) => {
  const n = (name || '').toLowerCase();
  if (ZODIAC_SIGNS.some((z) => n.includes(z))) return 'horoscope';
  return FAMILY_KEYWORDS.find((f) => n.includes(f)) || null;
};

export const capitalize = (s) => (
  s ? s.split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : ''
);

export const MAX_WIN_RANGES = [
  { key: 'all', label: 'Any', min: 0, max: Infinity },
  { key: '0-1000', label: '< 1,000×', min: 0, max: 1000 },
  { key: '1000-5000', label: '1K–5K×', min: 1000, max: 5000 },
  { key: '5000-10000', label: '5K–10K×', min: 5000, max: 10000 },
  { key: '10000+', label: '10K+×', min: 10000, max: Infinity },
];

const OTHER_PROVIDER_ID = 'others';

const roundTo = (value, digits = 2) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  const factor = 10 ** digits;
  return Math.round(n * factor) / factor;
};

/**
 * Parse a raw maxWin into an integer multiplier. The aggregator API returns
 * maxWin as a string, sometimes with a European thousands separator
 * ("10.000" = 10000). A dot followed by exactly 3 digits is a thousands
 * separator; "500000.00" (2-decimal) is a plain decimal and parses normally.
 */
const parseMaxWin = (raw) => {
  const s = String(raw ?? '').trim();
  if (/^\d{1,3}(\.\d{3})+$/.test(s)) return parseInt(s.replace(/\./g, ''), 10);
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n) : 0;
};

/**
 * Normalize a single raw game record from the API. Returns a NEW object;
 * does not mutate input. Coerces rtp into percent (handles 0–1 fractions),
 * rounds maxWin, parses created into a Date, detects family, normalizes
 * type (CRASH → CASUAL).
 */
export const normalizeGame = (raw) => {
  if (!raw || !raw.code) return null;
  const rtpRaw = Number(raw.rtp);
  let rtp = 0;
  if (Number.isFinite(rtpRaw) && rtpRaw > 0) {
    rtp = rtpRaw <= 1 ? roundTo(rtpRaw * 100, 2) : roundTo(rtpRaw, 2);
  }
  // maxWin: prefer the authoritative override (from RG asset manifests);
  // otherwise parse the API value (handles European thousands strings).
  const maxWin = Object.prototype.hasOwnProperty.call(MAX_WIN_OVERRIDES, raw.code)
    ? MAX_WIN_OVERRIDES[raw.code]
    : parseMaxWin(raw.maxWin);
  let created = null;
  if (raw.created) {
    created = raw.created instanceof Date ? raw.created : new Date(raw.created);
  }
  const type = raw.type === 'CRASH' ? 'CASUAL' : raw.type;
  const providerId = raw.provider?.id || raw.providerId || OTHER_PROVIDER_ID;
  return {
    ...raw,
    type,
    rtp,
    maxWin,
    created,
    providerId,
    family: detectFamily(raw.name),
    isPopular: Number(raw.popularIndex) > 0,
  };
};

const PORTFOLIO_PATH = '/api/v1/lobby-available-games';
// Rectangle no longer supports casual games, so the casual demo brand
// (cademo) is dropped and CASUAL/CRASH titles are filtered out below.
const PORTFOLIO_BRANDS = ['rcdemo'];
const PORTFOLIO_CACHE_KEY = 'showcase-portfolio-cache';
const PORTFOLIO_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

const safeStorage = () => {
  try { return typeof window !== 'undefined' ? window.localStorage : null; }
  catch (_) { return null; }
};

export const loadCachedPortfolio = () => {
  const ls = safeStorage();
  if (!ls) return null;
  try {
    const raw = ls.getItem(PORTFOLIO_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.games)) return null;
    if (!parsed.savedAt || Date.now() - parsed.savedAt > PORTFOLIO_CACHE_TTL_MS) return null;
    // re-hydrate Date instances
    return parsed.games.map((g) => (
      g.created ? { ...g, created: new Date(g.created) } : g
    ));
  } catch (_) { return null; }
};

const saveCachedPortfolio = (games) => {
  const ls = safeStorage();
  if (!ls || !Array.isArray(games) || games.length === 0) return;
  try {
    ls.setItem(PORTFOLIO_CACHE_KEY, JSON.stringify({
      savedAt: Date.now(),
      games,
    }));
  } catch (_) { /* quota / disabled — ignore */ }
};

const fetchBrandOnce = async (url, fetchImpl, logger, brand, attempt) => {
  try {
    const r = await fetchImpl(url);
    if (!r.ok) {
      logger.warn(`[showcase] ${brand} fetch ${r.status} (attempt ${attempt}): ${url}`);
      return { ok: false };
    }
    const data = await r.json();
    const count = Array.isArray(data?.data) ? data.data.length : 0;
    logger.info(`[showcase] ${brand} fetched ${count} games (attempt ${attempt})`);
    return { ok: true, data };
  } catch (err) {
    logger.warn(`[showcase] ${brand} fetch threw (attempt ${attempt}):`, err?.message || err);
    return { ok: false };
  }
};

const fetchBrandWithRetry = async (url, fetchImpl, logger, brand) => {
  const first = await fetchBrandOnce(url, fetchImpl, logger, brand, 1);
  if (first.ok) return first.data;
  const second = await fetchBrandOnce(url, fetchImpl, logger, brand, 2);
  if (second.ok) return second.data;
  logger.error(`[showcase] ${brand} gave up after 2 attempts`);
  return null;
};

const mergeByCode = (cachedGames, freshGames) => {
  const byCode = new Map();
  (cachedGames || []).forEach((g) => g?.code && byCode.set(g.code, g));
  // Fresh data wins on conflict.
  (freshGames || []).forEach((g) => g?.code && byCode.set(g.code, g));
  return Array.from(byCode.values());
};

/**
 * Fetch the showcase portfolio from each brand, log per-brand results,
 * dedupe + whitelist + normalize, and return the final list.
 *
 * Resilience strategy:
 * - If every brand fetch succeeded → trust the live result fully (replace cache).
 * - If only some brands succeeded → merge live result with cache so a partial
 *   API failure doesn't drop games we already knew about.
 * - If every brand fetch failed → return the cache untouched.
 */
export const fetchPortfolio = async ({
  params = {},
  activeGameCodes,
  fetchImpl = fetch,
  logger = console,
  cache = true,
} = {}) => {
  const allResults = await Promise.all(
    PORTFOLIO_BRANDS.map((brand) => fetchBrandWithRetry(
      `${PORTFOLIO_PATH}?${new URLSearchParams({ ...params, oc: brand })}`,
      fetchImpl,
      logger,
      brand,
    )),
  );
  const allBrandsOk = allResults.every((r) => r != null);
  const someBrandOk = allResults.some((r) => r != null);
  const fresh = dedupeAndNormalize(allResults, activeGameCodes);
  let brandStatus = 'no';
  if (allBrandsOk) brandStatus = 'all';
  else if (someBrandOk) brandStatus = 'partial';
  logger.info(
    `[showcase] portfolio: ${fresh.length} games after whitelist+dedupe (${brandStatus} brands ok)`,
  );

  if (!cache) return fresh;

  if (allBrandsOk && fresh.length > 0) {
    saveCachedPortfolio(fresh);
    return fresh;
  }

  const cached = loadCachedPortfolio() || [];

  if (!someBrandOk) {
    if (cached.length > 0) {
      logger.warn(`[showcase] all brand fetches failed — using ${cached.length} cached games`);
      return cached;
    }
    return fresh;
  }

  // Partial success: merge cache + fresh so we don't drop games the failing
  // brand would have provided.
  const merged = mergeByCode(cached, fresh);
  if (merged.length > fresh.length) {
    logger.warn(
      `[showcase] partial fetch — merged ${fresh.length} fresh + ${cached.length} cached → ${merged.length}`,
    );
  }
  saveCachedPortfolio(merged);
  return merged;
};

/**
 * Take the per-brand API responses, dedupe by code (first occurrence wins),
 * filter to ACTIVE games that are in the active-games whitelist, and
 * return a normalized list. Inputs that are null / errored are skipped.
 */
export const dedupeAndNormalize = (brandResults, activeGameCodes) => {
  const seen = new Set();
  const out = [];
  (brandResults || []).forEach((data) => {
    const items = Array.isArray(data?.data) ? data.data : [];
    items.forEach((raw) => {
      if (!raw || !raw.code) return;
      // Match upstream behavior: only games explicitly marked ACTIVE pass.
      // Games with missing/unknown status are NOT shown on the showcase.
      if (raw.status !== 'ACTIVE') return;
      // Rectangle no longer supports casual games (CRASH normalizes to CASUAL).
      if (raw.type === 'CASUAL' || raw.type === 'CRASH') return;
      if (activeGameCodes && !activeGameCodes.has(raw.code)) return;
      if (seen.has(raw.code)) return;
      seen.add(raw.code);
      const normalized = normalizeGame(raw);
      if (normalized) out.push(normalized);
    });
  });
  return out;
};

/**
 * Apply the legacy filter_by tab logic (provider tab or `type|CASUAL`)
 * and re-flag the top-10 popular RC games. Returns NEW objects.
 */
export const processGames = (games, { filterBy } = {}) => {
  if (!Array.isArray(games)) return [];
  let result = games.map((g) => ({ ...g }));

  // Re-flag isPopular for top 10 RC games by popularIndex
  const rc = result
    .filter((g) => g.provider?.code === 'rc')
    .sort((a, b) => (b.popularIndex || 0) - (a.popularIndex || 0));
  const topRcCodes = new Set(rc.slice(0, 10).map((g) => g.code));
  result = result.map((g) => (
    g.provider?.code === 'rc' ? { ...g, isPopular: topRcCodes.has(g.code) } : g
  ));

  if (filterBy && !['all', 'created', 'playCount'].includes(filterBy)) {
    const filtered = result.filter((x) => {
      if (filterBy.includes('type|')) return `type|${x.type}` === filterBy;
      return x.providerId === filterBy;
    });
    if (filtered.length > 0) {
      result = filtered;
    }
  }
  return result;
};

export const applySearch = (games, term) => {
  if (!term || !term.trim()) return games;
  const t = term.trim().toLowerCase();
  return games.filter((g) => (g.name || '').toLowerCase().includes(t));
};

export const applyFilters = (games, filters = {}) => {
  let result = games;
  const {
    type, vol, maxWin, family, provider,
  } = filters;

  if (type && type !== 'all') {
    result = result.filter((g) => g.type === type);
  }
  if (vol && vol !== 'all') {
    result = result.filter((g) => (g.volatility || '').toLowerCase() === vol);
  }
  if (maxWin && maxWin !== 'all') {
    const range = MAX_WIN_RANGES.find((r) => r.key === maxWin);
    if (range) {
      result = result.filter((g) => {
        const mw = Number(g.maxWin) || 0;
        return mw >= range.min && mw < range.max;
      });
    }
  }
  if (family && family !== 'all') {
    result = result.filter((g) => g.family === family);
  }
  if (provider && provider !== 'all') {
    result = result.filter((g) => g.provider?.name === provider);
  }
  return result;
};

const popularRankOf = (code, ranks) => (
  ranks && Object.prototype.hasOwnProperty.call(ranks, code) ? ranks[code] : 9999
);

export const applySort = (games, options = {}) => {
  const {
    sortBy, sortDirection = 'desc', market, marketRankings, globalRankings,
  } = options;
  if (!sortBy) return games;
  const result = [...games];

  if (sortBy === 'popular') {
    const useMarket = market && market !== 'all'
      && marketRankings && marketRankings[market];
    const ranks = useMarket ? marketRankings[market] : (globalRankings || {});
    result.sort((a, b) => {
      const ra = popularRankOf(a.code, ranks);
      const rb = popularRankOf(b.code, ranks);
      // For ranks (lower = better), 'desc' direction shows most popular first.
      return sortDirection === 'asc' ? rb - ra : ra - rb;
    });
    return result;
  }

  const field = sortBy === 'new' ? 'created' : sortBy;
  result.sort((a, b) => {
    const av = a[field];
    const bv = b[field];
    const an = av instanceof Date ? av.getTime() : Number(av) || 0;
    const bn = bv instanceof Date ? bv.getTime() : Number(bv) || 0;
    return sortDirection === 'asc' ? an - bn : bn - an;
  });
  return result;
};

/**
 * Derive the available filter pill options from the unfiltered list,
 * so they don't shrink as the user searches or applies other filters.
 */
export const computeFilterOptions = (games) => {
  const list = Array.isArray(games) ? games : [];
  const vols = [...new Set(list.map((g) => (g.volatility || '').toLowerCase()).filter(Boolean))];
  const families = [...new Set(list.map((g) => g.family).filter(Boolean))];
  const provs = [...new Set(list.map((g) => g.provider?.name).filter(Boolean))];
  const types = [...new Set(list.map((g) => g.type).filter(Boolean))];
  return {
    vols, families, provs, types,
  };
};

export const countActiveFilters = (filters = {}) => (
  Object.values(filters).filter((v) => v && v !== 'all').length
);

/**
 * The full pipeline used by the portfolio page:
 *   originalData → processGames → applySearch → applyFilters → applySort
 */
export const buildDisplayList = ({
  games,
  searchTerm = '',
  filterBy,
  filters = {},
  sort = {},
  marketRankings,
  globalRankings,
  market,
}) => {
  const processed = processGames(games, { filterBy });
  const searched = applySearch(processed, searchTerm);
  const filtered = applyFilters(searched, filters);
  return applySort(filtered, {
    ...sort, market, marketRankings, globalRankings,
  });
};
